import type { ReactNode } from 'react'
import type { ProductLookup } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import { MAX_CART_LINES, MAX_ITEM_QUANTITY } from '@/constants/site'

export type CartItem = {
    key: string
    productId: string
    slug: string
    name: string
    image: string | null
    price: number
    size: string | null
    color: string | null
    quantity: number
    stock: number
}

export type CartProductInput = Omit<CartItem, 'key' | 'quantity'>

type CartContextValue = {
    items: Array<CartItem>
    hydrated: boolean
    count: number
    subtotal: number
    isOpen: boolean
    openCart: () => void
    closeCart: () => void
    setOpen: (open: boolean) => void
    addItem: (product: CartProductInput, quantity: number) => { added: number, limited: boolean }
    updateQuantity: (key: string, quantity: number) => void
    removeItem: (key: string) => void
    clear: () => void
    syncProducts: (products: Array<ProductLookup>) => void
}

const STORAGE_KEY = 'sayeh-cart-v1'

const CartContext = createContext<CartContextValue | null>(null)

export const cartKey = (productId: string, size: string | null, color: string | null): string =>
    [productId, size ?? '', color ?? ''].join('|')

const maxFor = (stock: number): number => Math.max(0, Math.min(stock, MAX_ITEM_QUANTITY))

const sanitize = (value: unknown): Array<CartItem> => {
    if (!Array.isArray(value)) return []

    return value
        .filter((item): item is CartItem => Boolean(item) && typeof item === 'object' && typeof (item as CartItem).productId === 'string')
        .map(item => {
            const size = typeof item.size === 'string' ? item.size : null
            const color = typeof item.color === 'string' ? item.color : null
            const stock = Number.isFinite(item.stock) ? Math.max(0, Math.round(item.stock)) : 0

            return {
                key: cartKey(item.productId, size, color),
                productId: item.productId,
                slug: String(item.slug ?? ''),
                name: String(item.name ?? ''),
                image: typeof item.image === 'string' ? item.image : null,
                price: Number.isFinite(item.price) ? Math.max(0, Math.round(item.price)) : 0,
                size,
                color,
                quantity: Math.max(1, Math.min(Math.round(Number(item.quantity) || 1), MAX_ITEM_QUANTITY)),
                stock
            }
        })
        .slice(0, MAX_CART_LINES)
}

const persist = (items: Array<CartItem>): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    }
    catch {
        return
    }
}

export const CartProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [items, setItems] = useState<Array<CartItem>>([])
    const [hydrated, setHydrated] = useState(false)
    const [isOpen, setOpen] = useState(false)

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY)

            if (raw) setItems(sanitize(JSON.parse(raw)))
        }
        catch {
            setItems([])
        }

        setHydrated(true)

        const onStorage = (event: StorageEvent) => {
            if (event.key !== STORAGE_KEY) return

            try {
                setItems(event.newValue ? sanitize(JSON.parse(event.newValue)) : [])
            }
            catch {
                setItems([])
            }
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    const update = useCallback((updater: (current: Array<CartItem>) => Array<CartItem>) => {
        setItems(current => {
            const next = updater(current)

            persist(next)

            return next
        })
    }, [])

    const addItem = useCallback((product: CartProductInput, quantity: number) => {
        const key = cartKey(product.productId, product.size, product.color)
        const existing = items.find(item => item.key === key)
        const limit = maxFor(product.stock)
        const current = existing?.quantity ?? 0
        const desired = current + Math.max(1, Math.round(quantity))
        const nextQuantity = Math.min(desired, limit)
        const added = Math.max(0, nextQuantity - current)

        if (added > 0) {
            update(list => {
                const found = list.find(item => item.key === key)

                if (found) return list.map(item => (item.key === key ? { ...item, ...product, key, quantity: nextQuantity } : item))
                if (list.length >= MAX_CART_LINES) return list

                return [...list, { ...product, key, quantity: nextQuantity }]
            })
        }

        return { added, limited: desired > limit }
    }, [items, update])

    const updateQuantity = useCallback((key: string, quantity: number) => {
        update(list => list.map(item => {
            if (item.key !== key) return item

            const limit = Math.max(1, maxFor(item.stock))

            return { ...item, quantity: Math.max(1, Math.min(Math.round(quantity), limit)) }
        }))
    }, [update])

    const removeItem = useCallback((key: string) => {
        update(list => list.filter(item => item.key !== key))
    }, [update])

    const clear = useCallback(() => {
        update(() => [])
    }, [update])

    const syncProducts = useCallback((products: Array<ProductLookup>) => {
        const map = new Map(products.map(product => [product.id, product]))

        update(list => list.map(item => {
            const product = map.get(item.productId)

            if (!product || !product.is_active) return { ...item, stock: 0 }

            return {
                ...item,
                name: product.name,
                slug: product.slug,
                image: product.image,
                price: product.price,
                stock: product.stock,
                quantity: product.stock > 0 ? Math.min(item.quantity, Math.max(1, maxFor(product.stock))) : item.quantity
            }
        }))
    }, [update])

    const value = useMemo<CartContextValue>(() => ({
        items,
        hydrated,
        count: items.reduce((sum, item) => sum + item.quantity, 0),
        subtotal: items.reduce((sum, item) => sum + (item.stock > 0 ? item.price * item.quantity : 0), 0),
        isOpen,
        openCart: () => setOpen(true),
        closeCart: () => setOpen(false),
        setOpen,
        addItem,
        updateQuantity,
        removeItem,
        clear,
        syncProducts
    }), [items, hydrated, isOpen, addItem, updateQuantity, removeItem, clear, syncProducts])

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    )
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) throw new Error('useCart must be used inside CartProvider.')

    return context
}
